import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import GoToFolderNotification from './GoToFolderNotification';
import CreatedFolderNotification from './CreatedFolderNotification';
import DefaultNotification from './DefaultNotification';
import { store } from 'react-notifications-component';

import { AuthContext } from '../../util/context';
import CopyToClipboardNotification from './CopyToClipboardNotification';
import { FiCheck, FiX } from 'react-icons/fi';
import { PALE_WHITE_6 } from '../../appearance/Colors';

// Simple in-memory throttle registry to avoid spamming notifications
const lastShownAtByKey = new Map();
const THROTTLE_MS = 2000;

function useNotificationSystem() {
    const {t: lnT} = useTranslation();

    const {
        state: { treeData },
        context: { setSelectedFolder }
    } = React.useContext(AuthContext);

    const createNotificationId = () => {
        let notificationId = Math.floor(Math.random() * 100000000);
        return notificationId;
    };

    const showGoToFolderNotification = toFolder => {
        // Throttle by folder id and title
        const key = `${toFolder?.id || 'unknown'}:${toFolder?.title || ''}`;
        const now = Date.now();
        const last = lastShownAtByKey.get(key) || 0;
        if (now - last < THROTTLE_MS) {
            return;
        }
        lastShownAtByKey.set(key, now);
        console.log('this is the folder', toFolder);
        let notificationId = createNotificationId();
        store.addNotification({
            // just because we're showing the notification for creating the folder at the same
            id: notificationId,
            content: (
                <GoToFolderNotification
                    notificationId={notificationId}
                    toFolder={toFolder}
                    title={lnT('common:messageMovedTo', { value1: String(toFolder.title) })}
                    actionTitle={lnT('folders:viewFolder')}
                    actionClick={() => {
                        setSelectedFolder(toFolder);
                    }}
                    icon={<FiCheck color={PALE_WHITE_6} size={18}></FiCheck>}
                />
            ),
            // title: 'Wonderful!',
            // message: 'teodosii@react-notifications-component',
            // type: 'success',
            width: 300,
            insert: 'top',
            container: 'top-center',
            animationIn: ['animated', 'fadeIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
                duration: 3000,
                // onScreen: true,
                pauseOnHover: true,
                click: false
            }
        });
    };

    const movedFolderNotification = toFolder => {
        if (!toFolder.title) {
            let newFold = treeData.items[toFolder.id];
            if (newFold && newFold.data) {
                toFolder = newFold.data;
            }
        }
        let notificationId = createNotificationId();
        store.addNotification({
            // just because we're showing the notification for creating the folder at the same
            id: notificationId,
            content: (
                <GoToFolderNotification
                    notificationId={notificationId}
                    toFolder={toFolder}
                    title={lnT('common:folderMovedTo', { value1: String(toFolder.title) })}
                    actionTitle={lnT('folders:viewFolder')}
                    actionClick={() => {
                        setSelectedFolder(toFolder);
                    }}
                    icon={<FiCheck color={PALE_WHITE_6} size={18}></FiCheck>}
                />
            ),
            // title: 'Wonderful!',
            // message: 'teodosii@react-notifications-component',
            // type: 'success',
            width: 300,
            insert: 'top',
            container: 'top-center',
            animationIn: ['animated', 'fadeIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
                duration: 4000,
                // onScreen: true,
                pauseOnHover: true,
                click: false
            }
        });
    };

    const showCreatedFolderNotification = folder => {
        // The creating tab receives the same folder through both the mutation
        // callback and the NEW_FOLDER subscription. Show one notification for
        // that logical folder creation, not one per delivery mechanism.
        const key = `created-folder:${folder?.id || 'unknown'}`;
        const now = Date.now();
        const last = lastShownAtByKey.get(key) || 0;
        if (now - last < THROTTLE_MS) {
            return;
        }
        lastShownAtByKey.set(key, now);

        console.log('this is the folder', folder);
        let notificationId = createNotificationId();
        store.addNotification({
            id: notificationId,
            content: (
                <CreatedFolderNotification
                    notificationId={notificationId}
                    toFolder={folder}
                    setSelectedFolder={setSelectedFolder}
                    title={lnT('messages:created')}
                />
            ),
            // title: 'Wonderful!',
            // message: 'teodosii@react-notifications-component',
            // type: 'success',
            width: 300,
            insert: 'top',
            container: 'top-center',
            animationIn: ['animated', 'fadeIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
                duration: 1500,
                // onScreen: true,
                pauseOnHover: true,
                click: false
            }
        });
    };

    const showDefaultNotification = (id, title) => {
        console.log('this is the folder', id, title);
        store.addNotification({
            id: id,
            content: <DefaultNotification id={id} title={title} />,
            // title: 'Wonderful!',
            // message: 'teodosii@react-notifications-component',
            // type: 'success',
            width: 300,
            insert: 'top',
            container: 'top-center',
            animationIn: ['animated', 'fadeIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
                duration: 1500,
                // onScreen: true,
                pauseOnHover: false,
                click: false
            }
        });
    };

    const showCopyToClipboardNotification = () => {
        // console.log('this is the folder', id, title);
        let notificationId = createNotificationId();
        store.addNotification({
            id: notificationId,
            content: <CopyToClipboardNotification notificationId={notificationId} title={lnT('common:copiedToClipboard')} />,
            // title: 'Wonderful!',
            // message: 'teodosii@react-notifications-component',
            // type: 'success',
            width: 300,
            insert: 'top',
            container: 'top-center',
            animationIn: ['animated', 'fadeIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
                duration: 1500,
                // onScreen: true,
                pauseOnHover: false,
                click: false
            }
        });
    };

    return {
        showGoToFolderNotification,
        showDefaultNotification,
        showCopyToClipboardNotification,
        showCreatedFolderNotification,
        movedFolderNotification
    };
}

export default useNotificationSystem;
